import pegasus from '../../assets/scripts/global/01-pegasus';

/**
 * allModals
 *
 * Component code for creating all modals on the page
 *
 * 	Usage:
 * 	Add link with the attr 'data-pegasus-modal'.
 * 	Any values passed to the attr will be added as classes on the modal so it can be styled accordingly.
 * 	To include modal navigation, pass the class 'Modal--with-nav' to the data attr. This class will be picked up and the nav will be built.
 *
 * 	Example:
 *  <a href="#" data-pegasus-modal="Modal--team Modal--with-nav"></a>
 *
 * @since  2.0.0
 */
window.allModals = () => {
	return {
		init() {
			const _this = this;
			const modalSkeleton = pegasus.stringToHTML(document.getElementById('Modal__Skeleton').innerHTML);
			const modalLinks = _this.createModalLinks(document.querySelectorAll('[data-pegasus-modal]'));

			this._pendingHsForms = {};

			modalLinks.forEach((link, index) => {
				const modal = modalSkeleton.cloneNode(true);

				let cssClass = 'Modal',
					modalNav;

				//check for classes to add
				if (link.dataset && link.dataset.pegasusModal && link.dataset.pegasusModal.length) {
					cssClass += ' ' + link.dataset.pegasusModal;

					//if nav specified
					if (link.dataset.pegasusModal.includes('Modal--with-nav')) {
						modalNav = _this.buildModalNav(index, modalLinks);

						if (modalNav) {
							modal.querySelector('.Modal__navigation').append(modalNav);
						}
					}
				}

				//setup classes and id
				modal.className = cssClass;
				modal.id = link.dataset.pegasusModalId;

				//append modal template to page if it does nt already exist
				if (link.dataset && !document.getElementById(link.dataset.pegasusModalId)) {
					document.body.append(modal);
				}

				// Pre-fetch ≤4 modals when the browser is idle so the request
				// doesn't appear in the critical chain.
				if (modalLinks.length <= 4) {
					const prefetch = () => _this.loadModalContent(link.href, link.dataset.pegasusModalId);
					if ('requestIdleCallback' in window) {
						requestIdleCallback(prefetch, { timeout: 3000 });
					} else {
						setTimeout(prefetch, 2000);
					}
				}
			});

			// Deferred HubSpot form initialization.
			// Forms are only initialized when the modal is actually opened,
			// preventing the HubSpot SDK + reCAPTCHA from loading during pre-fetch.
			window.addEventListener('modalopen', (evt) => {
				const id = evt.detail?.modalID;
				if (!id) return;

				const pending = this._pendingHsForms[id];
				if (pending) {
					pending.opened = true;
					// Content already fetched — init now.
					if (pending.modalBody) {
						this.initHubspotForm(pending.modalBody, id);
						delete this._pendingHsForms[id];
					}
				} else {
					// Content not fetched yet (>4 modals: click triggers fetch + open).
					// Store the opened flag so .finally() can init when fetch completes.
					this._pendingHsForms[id] = { opened: true, modalBody: null };
				}
			});

			window.modal = this;
		},

		doesLinkAlreadyExist(link, linksArray) {
			return linksArray.find((element) => element.href === link.href);
		},

		createModalLinks(links) {
			const _this = this;
			const modalLinks = [];

			if (links.length) {
				links.forEach((link, index) => {
					const container = link;

					if (!link || link.tagName.toLowerCase() !== 'a') {
						const innerAnchor = container.querySelector('a[href]');
						if (!innerAnchor) return; // No actionable link

						// Ensure modal data attributes exist on the actionable anchor
						if (!innerAnchor.dataset.pegasusModal && container.dataset.pegasusModal) {
							innerAnchor.setAttribute('data-pegasus-modal', container.dataset.pegasusModal);
						}
						if (!innerAnchor.dataset.pegasusModalId && container.dataset.pegasusModalId) {
							innerAnchor.setAttribute('data-pegasus-modal-id', container.dataset.pegasusModalId);
						}

						link = innerAnchor;
					}

					// Validate the href — skip empty, fragment-only, or javascript: links.
					// We intentionally do NOT append ?modal=yes to the actual href so that
					// right-click → "Open in new tab" loads the full page.
					// The parameter is added at fetch-time in loadModalContent() instead.
					const rawHref = link.getAttribute('href') || '';

					if (rawHref === '' || rawHref.startsWith('#') || rawHref.toLowerCase().startsWith('javascript:')) {
						return;
					}

					try {
						new URL(rawHref, window.location.href);
					} catch (e) {
						return;
					}

					const existingLink = _this.doesLinkAlreadyExist(link, modalLinks);

					//check for existing link and copy id if true
					if (existingLink) {
						link.setAttribute('data-pegasus-modal-id', existingLink.dataset.pegasusModalId);
					} else {
						link.setAttribute('data-pegasus-modal-id', `modal-${index}`);
					}

					//if there are 5 or more modals, add 'click' to load more
					if (links.length > 4) {
						link.addEventListener('click', (e) => {
							e.preventDefault();
							_this.loadModalContent(link.href, link.dataset.pegasusModalId);
						});
					}

					//add event to emit 'modalopen' event for specified modal
					link.addEventListener('click', _this.emitModalOpen);

					//check we're not adding existing links.
					if (!existingLink) modalLinks.push(link);
				});
			}

			return modalLinks;
		},

		emitModalOpen(e) {
			e.preventDefault();

			const modalID = e.currentTarget.dataset.pegasusModalId;
			const myEvent = new CustomEvent('modalopen', {
				detail: { modalID },
			});

			window.dispatchEvent(myEvent);
		},

		buildModalNav(currentIndex, allLinksArray) {
			const _this = this;

			let prevLinkHTML, nextLinkHTML;

			//check if we're at the start or end
			let prevLink = currentIndex > 0 ? allLinksArray[currentIndex - 1] : '';
			let nextLink = currentIndex < allLinksArray.length - 1 ? allLinksArray[currentIndex + 1] : '';

			//check if links are modals with navs
			if (prevLink !== '') prevLink = prevLink.dataset.pegasusModal.includes('Modal--with-nav') ? prevLink : '';
			if (nextLink !== '') nextLink = nextLink.dataset.pegasusModal.includes('Modal--with-nav') ? nextLink : '';

			if (prevLink !== '') {
				const target = prevLink.dataset.pegasusModalId;
				const prevHref = prevLink.getAttribute('href') || '';

				prevLinkHTML = prevHref
					? `<a href="${prevHref}" class="prev-link arrow-btn" data-pegasus-modal-id="${target}"></a>`
					: `<span class="prev-link arrow-btn -disabled"></span>`;
			} else {
				prevLinkHTML = `<span class="prev-link arrow-btn -disabled"></span>`;
			}

			if (nextLink !== '') {
				const target = nextLink.dataset.pegasusModalId;
				const nextHref = nextLink.getAttribute('href') || '';

				nextLinkHTML = nextHref
					? `<a href="${nextHref}" class="next-link arrow-btn" data-pegasus-modal-id="${target}"></a>`
					: `<span class="next-link arrow-btn -disabled"></span>`;
			} else {
				nextLinkHTML = `<span class="next-link arrow-btn -disabled"></span>`;
			}

			//create modalNav DOM element
			const modalNav = pegasus.stringToHTML(`<div class="links">${prevLinkHTML} ${nextLinkHTML}</div>`);

			//set up modal open and ajax events on modal nav links
			modalNav.querySelectorAll('[data-pegasus-modal-id]').forEach((link) => {
				link.addEventListener('click', _this.emitModalOpen);

				link.addEventListener('click', (e) => {
					e.preventDefault();
					_this.loadModalContent(link.href, link.dataset.pegasusModalId);
				});
			});

			return modalNav;
		},

		loadModalContent(href, modalID) {
			const modalBody = document.querySelector(`#${modalID} .Modal__body`);

			// Skip if content already loaded or a fetch is in flight.
			if (modalBody?.dataset.pegasusLoaded || modalBody?.dataset.pegasusLoading) {
				return;
			}

			let url;
			try {
				url = new URL(href, window.location.href);
			} catch (e) {
				return;
			}
			// Append ?modal=yes at fetch-time only — the actual <a href> stays
			// clean so right-click → "Open in new tab" loads the full page.
			url.searchParams.set('modal', 'yes');

			modalBody.dataset.pegasusLoading = 'true';

			let lastModalBody = null;

			pegasus
				.fetchContent(url)
				.then((resp) => {
					if (!resp.ok) throw Error(resp.statusText);

					return resp.text();
				})
				.then((htmlResponse) => {
					const modalHTML = htmlResponse;

					const modalBody = document.querySelector(`#${modalID} .Modal__body`);

					modalBody.innerHTML = modalHTML;

					lastModalBody = modalBody;
					delete modalBody.dataset.pegasusLoading;
					modalBody.dataset.pegasusLoaded = 'true';

					return modalBody;
				})
				.then((modalBody) => {
					//reinit anything else here...
					pegasus.decodeAllCloudflareEmails(modalBody);

					//reinit cf7 forms
					if (typeof wpcf7 !== 'undefined' && wpcf7 !== null) {
						const cf7Forms = document.querySelectorAll(`#${modalID} .wpcf7 > form`);
						if (cf7Forms.length) cf7Forms.forEach((form) => wpcf7.init(form));
					}
				})
				.catch((error) => {
					delete modalBody.dataset.pegasusLoading;

					const errorContainer = modalBody.querySelector('.Modal__error');

					if (typeof errorContainer === 'undefined' || errorContainer === null) return;

					errorContainer.classList.remove('hidden');
				})
				.finally(() => {
					if (document.lazyLoadInstance) {
						document.lazyLoadInstance.update();
					}

					// Defer HubSpot initialization until the modal is opened.
					// Pre-fetched modals (≤4) load content ~1s after page load,
					// but we must not trigger the HubSpot SDK + reCAPTCHA until the user
					// actually opens the modal.
					if (lastModalBody) {
						const pending = this._pendingHsForms[modalID];
						if (pending && pending.opened) {
							// Modal already opened (>4 modals: click triggered open before fetch finished).
							this.initHubspotForm(lastModalBody, modalID);
							delete this._pendingHsForms[modalID];
						} else {
							// Modal not yet opened — store form data for the modalopen listener.
							this._pendingHsForms[modalID] = this._pendingHsForms[modalID] || {};
							this._pendingHsForms[modalID].modalBody = lastModalBody;
						}
					}
				});
		},
		initHubspotForm(modalBody, modalID) {
			if (!modalBody) {
				return;
			}

			const scripts = modalBody.querySelectorAll('script');

			let formScript = null;
			let hubspotSrc = '';

			scripts.forEach((script) => {
				if (script.src && script.src.includes('hsforms')) {
					hubspotSrc = script.src;
					this.injectHubspotScript(script.src);
				}

				const content = script.textContent || '';
				if (content.includes('hbspt.forms.create')) {
					formScript = script;
				}
			});

			if (!formScript) {
				return;
			}

			const scriptContent = formScript.textContent;

			const formId = this.getSubstringBetween(scriptContent, 'formId: "', '"');
			const portalId = this.getSubstringBetween(scriptContent, 'portalId: "', '"');
			let target = this.getSubstringBetween(scriptContent, 'target: "', '"');

			if (!target) {
				const existingWrapper = formScript.parentElement.querySelector('.hbspt-form');
				if (existingWrapper && existingWrapper.id) {
					target = `#${existingWrapper.id}`;
				} else {
					const generatedWrapper = document.createElement('div');
					generatedWrapper.id = `hubspot-form-${modalID}`;
					generatedWrapper.className = 'hbspt-form';
					formScript.insertAdjacentElement('afterend', generatedWrapper);
					target = `#${generatedWrapper.id}`;
				}
			}

			if (!formId || !portalId) {
				return;
			}

			// Already initialized — skip.
			if (modalBody.dataset.pegasusHubspotDone) {
				return;
			}

			modalBody.dataset.pegasusHubspotDone = 'true';

			this.ensureHubspotScriptLoaded(hubspotSrc)
				.then(() => {
					const targetElement = document.querySelector(target);

					if (!targetElement) {
						const fallback = document.createElement('div');
						fallback.id = `hubspot-form-${modalID}-fallback`;
						fallback.className = 'hbspt-form';
						modalBody.appendChild(fallback);
						target = `#${fallback.id}`;
					}

					window.hbspt.forms.create({
						portalId,
						formId,
						target,
						onFormSubmitted: () => {
							this.hideFormIntros(modalBody);
						},
					});
				})
				.catch(() => {
					// Allow retry on failure.
					delete modalBody.dataset.pegasusHubspotDone;
				});
		},
		hideFormIntros(container) {
			const scope = container || document;
			if (!scope) return;

			scope.querySelectorAll('.form-intro').forEach((intro) => {
				intro.style.display = 'none';
			});
		},
		normaliseHubspotSrc(src = '') {
			if (!src) return 'https://js.hsforms.net/forms/embed/v2.js';
			if (src.startsWith('//')) return `https:${src}`;
			if (!/^https?:\/\//i.test(src)) return `https://${src}`;
			return src;
		},
		injectHubspotScript(src) {
			const normalisedSrc = this.normaliseHubspotSrc(src);
			let existing = document.querySelector(`script[src="${normalisedSrc}"]`);

			if (!existing) {
				existing = document.createElement('script');
				existing.src = normalisedSrc;
				existing.async = true;
				existing.dataset.pegasusHubspot = 'true';
				document.head.appendChild(existing);
			}
		},
		ensureHubspotScriptLoaded(src) {
			const normalisedSrc = this.normaliseHubspotSrc(src);

			/**
			 * Detect the REAL HubSpot API — not our lazy-loading shim.
			 * The HubSpotLazyLoader stores the shim reference at
			 * window._pegasusHsShimCreate so we can distinguish it.
			 */
			const isRealApi = () =>
				window.hbspt && window.hbspt.forms && typeof window.hbspt.forms.create === 'function' && window.hbspt.forms.create !== window._pegasusHsShimCreate;

			// Already loaded — resolve immediately.
			if (isRealApi()) {
				return Promise.resolve(window.hbspt);
			}

			// Ensure the <script> tag exists (may already be injected by the lazy loader).
			this.injectHubspotScript(normalisedSrc);

			// Notify the lazy loader a modal needs HubSpot.
			window.dispatchEvent(new CustomEvent('modalcontentloaded'));

			// Poll until the real API is available.
			// This handles all timing scenarios:
			//  - Script not yet loaded  → poll catches it after load
			//  - Script already loaded by lazy loader → load event won't re-fire, poll catches it
			//  - Shim still active while replayWhenReady is polling → poll waits for real API
			return new Promise((resolve, reject) => {
				if (isRealApi()) {
					resolve(window.hbspt);
					return;
				}

				const poll = setInterval(() => {
					if (isRealApi()) {
						clearInterval(poll);
						clearTimeout(timeout);
						resolve(window.hbspt);
					}
				}, 50);

				const timeout = setTimeout(() => {
					clearInterval(poll);
					reject(new Error('HubSpot script load timeout'));
				}, 10000);
			});
		},
		getSubstringBetween(str, startDelimiter, endDelimiter) {
			// Find the start position of the substring
			const startIndex = str.indexOf(startDelimiter);
			if (startIndex === -1) {
				// If the start delimiter is not found, return an empty string or handle error
				return '';
			}

			// Find the end position of the substring
			const endIndex = str.indexOf(endDelimiter, startIndex + startDelimiter.length);
			if (endIndex === -1) {
				// If the end delimiter is not found, return an empty string or handle error
				return '';
			}

			// Extract the substring between the start and end delimiters
			const substring = str.substring(startIndex + startDelimiter.length, endIndex);

			return substring;
		},
	};
};

/**
 * singleModal
 *
 * Component code for a single modal
 *
 * @since  2.0.0
 */
window.singleModal = () => {
	return {
		open: false,

		handleModalOpen(evt) {
			//only run on actual modals, not skeleton
			if (this.$el.classList.contains('ModalSkeleton')) return;

			document.body.classList.add('modal-open');
			document.body.style.overflow = 'hidden';
			this.open = this.$el.id === evt.detail.modalID;
		},

		handleModalClose() {
			document.body.classList.remove('modal-open');
			document.body.style.overflow = '';
			this.open = false;
		},
	};
};
